/**
 * The pairing payload the scanner scans: THE BARE TOKEN, and nothing around it.
 *
 * The token from POST /auth/devices/pair-token is one-time and short-lived
 * (api/src/db/devices.ts's `createPairingCode`, PAIRING_CODE_TTL_MS = 10
 * minutes; `generateToken()` = 32 random bytes as base64url, 43 characters).
 * It is encoded RAW into the QR -- not wrapped in a URL, not in a deep link,
 * and NOT in a JSON envelope:
 *
 *  - a URL would put a live credential into a browser history, a Referer and
 *    any URL-preview scanner between the phone and the app;
 *  - the scanner claims it with POST /auth/devices/claim, which takes
 *    `{ token }` and nothing else (api/src/authRoutes.ts:229-245). A wrapper
 *    would only be something the scanner has to strip.
 *
 * WHY NOT THE `{"v":1,"token":…}` ENVELOPE the task brief specified
 * (controller ruling, verified against the as-built scanner, and BINDING over
 * the brief): phase 4's `web/src/PairDevice.jsx` (phase4/scanner @ 043cb33)
 * does `onDetected(text) -> pair(text) -> claimPairing(String(text).trim())`,
 * which POSTs the ENTIRE scanned string as `token`. There is no unwrapping
 * step anywhere on that path. An envelope would therefore be sent verbatim as
 * the token, `claimPairingCode` would hash a JSON document, find no matching
 * `code_hash`, and answer 404 `invalid_or_used_token` -- pairing broken in
 * production, silently, for every device, with the scanner's own error copy
 * ("get a fresh code") pointing the operator at the one thing that cannot
 * help. The same string is also what an operator types into that view's
 * manual-entry field, which is the admin's text fallback on the other side of
 * the glass.
 *
 * VERSIONING LATER IS A JOINT CHANGE, deliberately not built now. The two
 * forms are prefix-discriminable -- a JSON envelope starts with '{', which is
 * not in the base64url alphabet -- so a future scanner can branch on the first
 * character and accept both, and only once THAT scanner is deployed may the
 * admin start emitting the envelope. Adding the wrapper here first would break
 * every device in the field; there is no admin-only version of this change.
 */

export function buildPairingPayload(token: string): string {
  // An empty token is a bug upstream (a 200 with no `token`, or a call before
  // the mutation resolved). Rendering an empty QR would be a support call
  // about a camera; throwing is the honest failure.
  if (!token) throw new Error('buildPairingPayload: empty token');
  // Returned byte-identical to what the API minted -- no prefix, no wrapper,
  // and no trimming: `claimPairingCode` hashes the raw string, so any
  // transformation at all is a token the server cannot match.
  return token;
}
